import { useEffect, useState } from "react";
import { Menu, UserPlus, ShieldCheck, Shield, Ban, KeyRound, Trash2, Loader2, Check, X, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls = "bg-[#0D0F18] border border-slate-800 focus:border-indigo-500 outline-none rounded-xl px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600";
const genPassword = () => Array.from(crypto.getRandomValues(new Uint8Array(10)), (b) => "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789".charAt(b % 54)).join("");

export default function AdminPage({ onOpenSidebar }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: genPassword(), role: "user" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch((err) => { toast.error(formatApiError(err)); setUsers([]); });
  }, []);

  const patchLocal = (id, patch) => setUsers((l) => l.map((u) => (u.id === id ? { ...u, ...patch } : u)));

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", form);
      setUsers((l) => [data, ...l]);
      const creds = `${form.email} / ${form.password}`;
      try { await navigator.clipboard.writeText(creds); } catch (_) {}
      toast.success(`Account created — credentials copied`, { description: creds, duration: 10000 });
      setForm({ name: "", email: "", password: genPassword(), role: "user" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const update = async (u, patch, okMsg) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, patch);
      patchLocal(u.id, data);
      toast.success(okMsg);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email} and all their conversations? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((l) => l.filter((x) => x.id !== u.id));
      toast.success("User deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="flex-1 overflow-y-auto relative" data-testid="admin-view">
      <div className="absolute inset-x-0 top-0 h-[300px] grid-bg pointer-events-none" />
      <header className="sticky top-0 z-10 h-16 flex items-center px-4 lg:px-10 bg-[#090A0F]/70 backdrop-blur-xl border-b border-slate-800/60">
        <button onClick={onOpenSidebar} className="lg:hidden mr-3 text-slate-400 hover:text-white" data-testid="open-sidebar-btn"><Menu className="w-5 h-5" /></button>
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400">Admin · Users</p>
        {users && <span className="ml-3 text-xs text-slate-500" data-testid="admin-user-count">{users.length} accounts</span>}
      </header>

      <div className="relative max-w-6xl mx-auto px-4 lg:px-10 py-10">
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-100 rise">Manage users</h1>
        <p className="mt-3 text-sm text-slate-400 max-w-xl">Add accounts, disable access, reset passwords or promote admins. Passwords are shown once — copy them to hand over.</p>

        <form onSubmit={create} className="mt-8 p-5 rounded-2xl bg-[#11131E] border border-slate-800/80 grid grid-cols-1 md:grid-cols-[1fr_1.3fr_1fr_auto_auto] gap-3 items-end" data-testid="admin-create-form">
          <label className="block"><span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">Name</span><input data-testid="admin-name-input" className={`${inputCls} w-full`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" required /></label>
          <label className="block"><span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">Email</span><input data-testid="admin-email-input" type="email" className={`${inputCls} w-full`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@company.com" required /></label>
          <label className="block"><span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">Temporary password</span><input data-testid="admin-password-input" className={`${inputCls} w-full font-mono`} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} minLength={6} required /></label>
          <label className="block"><span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">Role</span>
            <select data-testid="admin-role-select" className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}><option value="user">User</option><option value="admin">Admin</option></select>
          </label>
          <button type="submit" disabled={busy} data-testid="admin-create-btn" className="inline-flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Add user
          </button>
        </form>

        <div className="mt-8 rounded-2xl border border-slate-800/80 overflow-hidden" data-testid="admin-user-table">
          <div className="hidden md:grid grid-cols-[1.4fr_1fr_0.6fr_0.9fr_auto] gap-3 px-5 py-3 text-[10px] font-semibold uppercase tracking-widest text-slate-500 bg-[#0D0F18]">
            <span>User</span><span>Last login</span><span>Status</span><span>Role</span><span className="text-right">Actions</span>
          </div>
          {users?.map((u) => <UserRow key={u.id} u={u} me={me} onUpdate={update} onDelete={remove} />)}
          {users && users.length === 0 && <p className="p-6 text-sm text-slate-500">No users.</p>}
        </div>
      </div>
    </div>
  );
}

const UserRow = ({ u, me, onUpdate, onDelete }) => {
  const [resetting, setResetting] = useState(false);
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const isMe = u.id === me?.id;

  const startReset = () => { setPw(genPassword()); setResetting(true); };
  const doReset = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/users/${u.id}/password`, { password: pw });
      try { await navigator.clipboard.writeText(`${u.email} / ${pw}`); } catch (_) {}
      toast.success("Password reset — new credentials copied", { description: `${u.email} / ${pw}`, duration: 10000 });
      setResetting(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <div className={`grid grid-cols-1 md:grid-cols-[1.4fr_1fr_0.6fr_0.9fr_auto] gap-3 px-5 py-4 border-t border-slate-800/80 items-center ${u.disabled ? "opacity-60" : ""}`} data-testid={`admin-user-row-${u.id}`}>
      <div className="min-w-0">
        <p className="text-sm text-slate-100 truncate">{u.name} {isMe && <span className="text-[10px] text-indigo-300">(you)</span>}</p>
        <p className="text-xs text-slate-500 truncate" data-testid={`admin-user-email-${u.id}`}>{u.email}</p>
      </div>
      <p className="text-xs text-slate-400">{u.last_login ? new Date(u.last_login).toLocaleString() : "Never"}</p>
      <span data-testid={`admin-user-status-${u.id}`} className={`inline-flex w-fit items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${u.disabled ? "bg-rose-500/10 text-rose-300 border-rose-500/30" : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"}`}>{u.disabled ? "Disabled" : "Active"}</span>
      <button onClick={() => onUpdate(u, { role: u.role === "admin" ? "user" : "admin" }, u.role === "admin" ? "Admin role removed" : "Promoted to admin")} disabled={isMe} data-testid={`admin-user-role-${u.id}`} title={isMe ? "You can't change your own role" : "Toggle admin role"}
        className={`inline-flex w-fit items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border transition-colors disabled:cursor-not-allowed ${u.role === "admin" ? "bg-indigo-500/15 text-indigo-200 border-indigo-500/40" : "bg-[#0D0F18] text-slate-400 border-slate-800 hover:text-white"}`}>
        {u.role === "admin" ? <ShieldCheck className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />} {u.role === "admin" ? "Admin" : "User"}
      </button>
      <div className="flex items-center justify-end gap-1 flex-wrap">
        {resetting ? (
          <div className="flex items-center gap-1" data-testid={`admin-reset-panel-${u.id}`}>
            <input value={pw} onChange={(e) => setPw(e.target.value)} minLength={6} className={`${inputCls} w-40 font-mono text-xs py-1.5`} data-testid={`admin-reset-input-${u.id}`} />
            <button onClick={doReset} disabled={saving || pw.length < 6} data-testid={`admin-reset-confirm-${u.id}`} className="p-1.5 rounded-lg bg-indigo-500 text-white hover:bg-indigo-600 disabled:opacity-50" aria-label="Confirm reset">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}</button>
            <button onClick={() => setResetting(false)} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#1F2338]" aria-label="Cancel"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <>
            <button onClick={startReset} data-testid={`admin-user-reset-${u.id}`} title="Reset password" className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-[#1F2338] transition-colors" aria-label="Reset password"><KeyRound className="w-4 h-4" /></button>
            <button onClick={() => onUpdate(u, { disabled: !u.disabled }, u.disabled ? "Account re-enabled" : "Account disabled")} disabled={isMe} data-testid={`admin-user-disable-${u.id}`} title={u.disabled ? "Enable account" : "Disable account"} className={`p-2 rounded-lg transition-colors disabled:opacity-40 ${u.disabled ? "text-emerald-300 hover:bg-emerald-500/10" : "text-slate-400 hover:text-amber-300 hover:bg-amber-500/10"}`} aria-label="Toggle disabled">
              {u.disabled ? <Check className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
            </button>
            <button onClick={() => onDelete(u)} disabled={isMe} data-testid={`admin-user-delete-${u.id}`} title="Delete user" className="p-2 rounded-lg text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition-colors disabled:opacity-40" aria-label="Delete user"><Trash2 className="w-4 h-4" /></button>
          </>
        )}
      </div>
    </div>
  );
};
